import { Moon } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/common/ui/card";
import { Skeleton } from "@/common/ui/skeleton";

import type { WhoopSleep } from "../devices-types";

interface SleepCardProps {
  sleep: WhoopSleep[];
  isLoading: boolean;
}

function formatDuration(millis: number): string {
  const hours = Math.floor(millis / 3_600_000);
  const minutes = Math.floor((millis % 3_600_000) / 60_000);
  return `${hours}h ${minutes}m`;
}

export function SleepCard({ sleep, isLoading }: SleepCardProps): React.ReactElement {
  const latest = sleep[0];

  return (
    <Card data-testid="sleep-card">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">Sleep</CardTitle>
        <Moon className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-20 w-full" />
        ) : latest ? (
          <div className="space-y-3">
            <div data-testid="sleep-duration" className="text-3xl font-bold">
              {formatDuration(
                latest.score.stage_summary.total_in_bed_time_milli -
                  latest.score.stage_summary.total_awake_time_milli
              )}
            </div>
            {latest.score.sleep_performance_percentage !== null && (
              <div className="text-sm text-muted-foreground">
                Performance:{" "}
                <span data-testid="sleep-performance" className="font-medium text-foreground">
                  {latest.score.sleep_performance_percentage}%
                </span>
              </div>
            )}
            <div className="space-y-1">
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">Light</span>
                <span data-testid="sleep-light">
                  {formatDuration(latest.score.stage_summary.total_light_sleep_time_milli)}
                </span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">REM</span>
                <span data-testid="sleep-rem">
                  {formatDuration(latest.score.stage_summary.total_rem_sleep_time_milli)}
                </span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">Deep (SWS)</span>
                <span data-testid="sleep-deep">
                  {formatDuration(latest.score.stage_summary.total_slow_wave_sleep_time_milli)}
                </span>
              </div>
            </div>
            <div className="text-xs text-muted-foreground">
              Respiratory rate: {latest.score.respiratory_rate} breaths/min
            </div>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No sleep data available</p>
        )}
      </CardContent>
    </Card>
  );
}
